import { Box, Button, Callout, Checkbox, Intent, Label, Select, Text } from "@blasterjs/core";
import React, { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Ok } from "ts.data.json";
import { caseStatusDecoder } from "../decoders";
import { CaseStatus, formatCaseStatus, StudyView, userLabel } from "../models";
import { RolePermissions } from "../permissions";

import CaseDialog from "../components/CaseDialog";
import CasesTable from "../components/CasesTable";
import DebouncedTextInput from "../components/DebouncedTextInput";
import Page, {
  PageActions,
  PageHeader,
  PageHeading,
  PageTabs,
  PageBanner,
  PageBannerHeading
} from "../components/Page";
import { TableContainer, TableFilters } from "../components/Table";
import Content from "../components/Content";
import ImagesTable from "../components/ImagesTable";
import UploadDialog from "../components/UploadDialog";
import { useAppDispatch, useAppSelector } from "../hooks";
import { studyFetchRequest } from "../slices/studyConfiguration";
import { studyFetch } from "../slices/studies";
import { clearCaseFilters, setCaseFiltersAndFetch } from "../slices/cases";
import { clearImageFilters, setImageFiltersAndFetch } from "../slices/images";
import { openUploadDialog } from "../slices/uploadDialog";
import { openCreateCaseDialog } from "../slices/caseDialog";
import { openUploadSampleDataDialog } from "../slices/uploadSampleDataDialog";
import UploadSampleDataDialog from "../components/UploadSampleDataDialog";

enum Routes {
  Cases = "cases",
  Images = "images"
}

const Study = () => {
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector(state => state.auth.loggedInUser);

  const studyView = useAppSelector(state => state.studies.study);
  const imageFilters = useAppSelector(state => state.images.filters);
  const caseFilters = useAppSelector(state => state.cases.filters);
  const userisReader: boolean =
    "resource" in loggedInUser && loggedInUser.resource && loggedInUser.resource.isCR();

  const params = useParams();
  const id: string = params.id || "no-id";
  const tab: string = params.tab || Routes.Cases;

  useEffect(() => {
    dispatch(studyFetch(id));
    dispatch(studyFetchRequest(id));
  }, [id]);

  useEffect(() => {
    if ("resource" in studyView && imageFilters.search && tab === Routes.Images) {
      setTimeout(() => {
        const input = document.querySelector('input[placeholder="Search"]') as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 200);
    }
  }, [studyView, imageFilters.search, tab]);

  useEffect(() => {
    if ("resource" in studyView && caseFilters.procId && tab === Routes.Cases) {
      setTimeout(() => {
        const input = document.querySelector('input[placeholder="Search"]') as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 200);
    }
  }, [studyView, caseFilters.procId, tab]);

  const createCase = (studyView: StudyView) => () =>
    dispatch(openCreateCaseDialog(studyView.study.id));

  const createCaseButton =
    tab === Routes.Cases &&
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.S_CaseListTab_CreateCase]) &&
    "resource" in studyView ? (
      <Box>
        <Button
          iconBefore="plus"
          onClick={createCase(studyView.resource)}
          appearance="prominent"
          intent="primary"
        >
          Create case
        </Button>
        <CaseDialog />
      </Box>
    ) : null;

  const uploadImages = (studyView: StudyView) => () =>
    dispatch(openUploadDialog({ studyId: studyView.study.id }));

  const uploadSampleData = () => dispatch(openUploadSampleDataDialog());

  const uploadSampleDataButton = tab === Routes.Images &&
    "resource" in loggedInUser &&
    loggedInUser.resource.can([
      RolePermissions.IM_ImagesListTab_UploadImagestoAssignedStudiesOnly
    ]) &&
    "resource" in studyView && (
      <Box>
        <Button
          style={{ marginRight: "12px" }}
          iconBefore="upload"
          onClick={uploadSampleData}
          appearance="prominent"
          intent="primary"
        >
          Upload Sample Data
        </Button>
        <UploadSampleDataDialog />
        &nbsp;
      </Box>
    );

  const uploadImageButton =
    tab === Routes.Images &&
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.S_ImageListTab_UploadImage]) &&
    "resource" in studyView ? (
      <Box>
        <Button
          iconBefore="upload"
          onClick={uploadImages(studyView.resource)}
          appearance="prominent"
          intent="primary"
        >
          Upload image
        </Button>
        <UploadDialog />
      </Box>
    ) : null;

  const setImageSearchTerms = (searchTerms: string) => {
    dispatch(
      setImageFiltersAndFetch({
        studyId: id,
        filters: {
          ...imageFilters,
          search: searchTerms
        }
      })
    );
  };

  const setHasQueriesFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      setImageFiltersAndFetch({
        studyId: id,
        filters: {
          ...imageFilters,
          hasQueries: e.target.checked || undefined
        }
      })
    );
  };

  const setIsUnassignedFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      setImageFiltersAndFetch({
        studyId: id,
        filters: {
          ...imageFilters,
          isUnassigned: e.target.checked || undefined
        }
      })
    );
  };

  const setCasesSearchTerms = (searchTerms: string) => {
    dispatch(
      setCaseFiltersAndFetch({
        studyId: id,
        caseFilters: {
          procId: searchTerms || undefined,
          status: caseFilters.status,
          assignee: caseFilters.assignee
        }
      })
    );
  };

  const onSelectStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const caseStatusResult = caseStatusDecoder.decode(e.target.value);

    dispatch(
      setCaseFiltersAndFetch({
        studyId: id,
        caseFilters: {
          procId: caseFilters.procId,
          status: caseStatusResult instanceof Ok ? caseStatusResult.value : undefined,
          assignee: caseFilters.assignee
        }
      })
    );
  };

  const onSelectAssignee = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      setCaseFiltersAndFetch({
        studyId: id,
        caseFilters: {
          procId: caseFilters.procId,
          status: caseFilters.status,
          assignee: e.target.value || undefined
        }
      })
    );
  };

  const qcStatusSelect =
    "resource" in loggedInUser && !loggedInUser.resource.isCR() ? (
      <Select onChange={onSelectStatus} value={caseFilters.status || ""}>
        <option key="" value="">
          All statuses
        </option>
        {Object.values(CaseStatus)
          .filter(status => status !== CaseStatus.Invalid)
          .map(status => {
            return (
              <option key={status} value={status}>
                {formatCaseStatus(status)}
              </option>
            );
          })}
      </Select>
    ) : null;

  const assigneeSelect =
    "resource" in studyView && "readers" in studyView.resource ? (
      <Select onChange={onSelectAssignee} value={caseFilters.assignee || ""}>
        <option key="" value="">
          Any assignee
        </option>
        {studyView.resource.readers.map(reader => {
          return (
            <option key={reader.id} value={reader.id}>
              Assigned to{" "}
              {userLabel({
                ...reader,
                workAsRole: null,
                actions: null,
                isSystemAdmin: null
              })}
            </option>
          );
        })}
        <option key="UNASSIGNED" value="UNASSIGNED">
          Unassigned
        </option>
      </Select>
    ) : null;

  const userCanSeeCases =
    "resource" in loggedInUser
      ? loggedInUser.resource.can([
          RolePermissions.S_CaseListTab_ViewallCasesUnderAssignedStudies,
          RolePermissions.S_CaseListTab_ViewAssignedCasesonly
        ])
      : false;

  const userCanSeeImages =
    "resource" in loggedInUser
      ? loggedInUser.resource.can([RolePermissions.S_ImageListTab_ViewImageTab])
      : false;

  const permissionDenied = !userCanSeeCases && !userCanSeeImages;

  const navigateTo =
    "resource" in loggedInUser && (userCanSeeCases || userCanSeeImages) ? (
      (tab === undefined && userCanSeeCases) || (tab === Routes.Images && !userCanSeeImages) ? (
        <Navigate to={`/studies/${id}/cases`} />
      ) : (tab === undefined || tab === Routes.Cases) && !userCanSeeCases ? (
        <Navigate to={`/studies/${id}/images`} />
      ) : tab !== Routes.Cases && tab !== Routes.Images ? (
        <Navigate to="/" />
      ) : null
    ) : permissionDenied ? (
      <Navigate to="/accessDenied" />
    ) : null;

  const table =
    "resource" in loggedInUser ? (
      tab === Routes.Cases && userCanSeeCases ? (
        <CasesTable studyId={id} loggedInUser={loggedInUser.resource} userIsReader={userisReader} />
      ) : (
        <ImagesTable studyId={id} loggedInUser={loggedInUser.resource} />
      )
    ) : null;

  const onClearFilterClick = () => {
    if (tab == Routes.Cases) {
      dispatch(clearCaseFilters());
      dispatch(setCaseFiltersAndFetch({ studyId: id, caseFilters: {} }));
    } else {
      dispatch(clearImageFilters());
      dispatch(setImageFiltersAndFetch({ studyId: id, filters: {} }));
    }
  };

  return navigateTo ? (
    navigateTo
  ) : (
    <Page>
      <Content isLoading={"isPending" in studyView || "isPending" in loggedInUser || !!navigateTo}>
        {"resource" in studyView && "resource" in loggedInUser ? (
          <Box style={{ padding: "0 2rem 4rem" }}>
            {studyView.resource.study.onHold ? (
              <PageBanner>
                <PageBannerHeading>
                  On hold {studyView.resource.study.onHoldReason}
                </PageBannerHeading>
              </PageBanner>
            ) : null}
            <PageHeader>
              <PageHeading>{studyView.resource.study.name}</PageHeading>
              <PageActions>
                {"resource" in loggedInUser &&
                  loggedInUser.resource.can([RolePermissions.S_Main_EditStudy]) && (
                    <Link
                      to={`/studies/${id}/configure/settings`}
                      style={{ textDecoration: "none" }}
                    >
                      <Button iconBefore="edit">Edit Study</Button>
                    </Link>
                  )}
              </PageActions>
            </PageHeader>
            <PageTabs
              offsetTabs={true}
              links={[
                ...(userCanSeeCases
                  ? [
                      {
                        to: `/studies/${id}/${Routes.Cases}`,
                        label: "Cases"
                      }
                    ]
                  : []),
                ...(userCanSeeImages
                  ? [
                      {
                        to: `/studies/${id}/${Routes.Images}`,
                        label: "Images"
                      }
                    ]
                  : [])
              ]}
            />
            <TableContainer>
              <TableFilters>
                <Box display="flex" width="100%">
                  {tab === Routes.Images ? (
                    <>
                      <DebouncedTextInput
                        width="auto"
                        defaultValue={imageFilters.search || ""}
                        placeholder={"Search"}
                        onValueChange={(text: string) => setImageSearchTerms(text.trim())}
                      />
                      {userCanSeeCases ? (
                        <Box paddingLeft="10px">
                          <Label>
                            <Checkbox
                              checked={imageFilters.isUnassigned || false}
                              onChange={setIsUnassignedFilter}
                              ml={1}
                              mr={1}
                            />
                            <Text
                              color="gray600"
                              style={{ top: "2px", position: "relative", fontWeight: 400 }}
                            >
                              Only unassigned
                            </Text>
                          </Label>
                        </Box>
                      ) : null}
                      <Box paddingLeft="10px">
                        <Label>
                          <Checkbox
                            checked={imageFilters.hasQueries || false}
                            onChange={setHasQueriesFilter}
                            ml={1}
                            mr={1}
                          />
                          <Text
                            color="gray600"
                            style={{ top: "2px", position: "relative", fontWeight: 400 }}
                          >
                            Only images with queries
                          </Text>
                        </Label>
                      </Box>
                      <Box paddingLeft="10px">
                        <Button
                          className="query-blue-button"
                          ml="auto"
                          onClick={onClearFilterClick}
                        >
                          Clear
                        </Button>
                      </Box>
                      <Box ml="auto">{uploadSampleDataButton}</Box>
                      <Box>{uploadImageButton}</Box>
                    </>
                  ) : (
                    <>
                      <DebouncedTextInput
                        width="auto"
                        defaultValue={caseFilters.procId || ""}
                        placeholder={"Search"}
                        onValueChange={(text: string) => setCasesSearchTerms(text.trim())}
                      />
                      <Box paddingLeft="10px">{qcStatusSelect}</Box>
                      <Box paddingLeft="10px">{assigneeSelect}</Box>
                      <Box paddingLeft="10px">
                        <Button
                          className="query-blue-button"
                          ml="auto"
                          onClick={onClearFilterClick}
                        >
                          Clear
                        </Button>
                      </Box>
                      <Box ml="auto">{createCaseButton}</Box>
                    </>
                  )}
                </Box>
              </TableFilters>
              {table}
            </TableContainer>
          </Box>
        ) : (
          <Box>
            <Callout intent={Intent.WARNING}>Study not found</Callout>
          </Box>
        )}
      </Content>
    </Page>
  );
};

export default Study;
